import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError } from '../lib/api';
import { meKey, useMe } from '../lib/session';
import { Button } from '../ui/controls';
import { ErrorNotice, Notice } from '../ui/feedback';
import { PortalLayout } from './PortalLayout';
import { LOGIN_ERRORS, WEB_COMMAND } from './sections';

/**
 * Bestätigung des Einmal-Links aus dem Spiel (architecture.md 4.2): `/login/game#<token>`.
 * Der Token steht im Fragment, geht also nie per URL an einen Server. Die Seite zeigt, für wen der Link gilt;
 * erst „Anmelden“ verbraucht ihn. So fällt ein untergeschobener Link auf, und Link-Vorschauen verbrauchen nichts.
 */
export function GameLoginPage() {
  // Token einmal aus dem Fragment lesen und aus Adressleiste und Verlauf entfernen
  const [token] = useState(() => {
    const t = window.location.hash.slice(1);
    if (t) window.history.replaceState(null, '', window.location.pathname);
    return t;
  });
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const check = useQuery({
    queryKey: ['game-link', token],
    queryFn: () => api.post<{ name: string }>('/web/auth/game/check', { token }),
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
  });
  const redeem = useMutation({
    mutationFn: () => api.post<{ redirect: string }>('/web/auth/game', { token }),
    onSuccess: async ({ redirect }) => {
      await qc.invalidateQueries({ queryKey: meKey });
      navigate(redirect, { replace: true });
    },
  });

  const error = check.error ?? redeem.error;
  const known = error instanceof ApiError && (error.code === 'WEB_LINK_INVALID' || error.code === 'ACCOUNT_SUSPENDED');

  return (
    <PortalLayout>
      <section>
        <h1>Im Browser anmelden</h1>
        {(token === '' || known) && (
          <>
            <Notice>
              {error instanceof ApiError && error.code === 'ACCOUNT_SUSPENDED' ? LOGIN_ERRORS.suspended : LOGIN_ERRORS.link}
            </Notice>
            <p className="lead">
              Im Spiel <span className="mono">{WEB_COMMAND}</span> eingeben und den neuen Link anklicken.
            </p>
            <p>
              <Link to="/">Zur Startseite</Link>
            </p>
          </>
        )}
        {error && !known && <ErrorNotice error={error} onRetry={() => (check.isError ? check.refetch() : redeem.reset())} />}
        {token !== '' && check.isPending && <p className="muted">Link wird geprüft…</p>}
        {check.data && !known && (
          <>
            <p className="lead">
              Als <b>{check.data.name}</b> anmelden?
            </p>
            {me.data && me.data.name !== check.data.name && (
              <Notice tone="warn">
                <span>
                  Du bist gerade als <b>{me.data.name}</b> angemeldet. Anmelden wechselt das Konto.
                </span>
              </Notice>
            )}
            <div>
              <Button variant="primary" onClick={() => redeem.mutate()} busy={redeem.isPending} busyText="Wird angemeldet…">
                Anmelden
              </Button>
            </div>
            <p className="muted">
              Nicht dein Minecraft-Name? Dann schließe diese Seite. Den Link hat dir jemand anderes geschickt.
            </p>
          </>
        )}
      </section>
    </PortalLayout>
  );
}
